import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { recordPendingLoginReturn } from '../utils/pendingLogin';

export const Login: React.FC = () => {
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { initiateLogin, isAuthenticated } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/admin', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const handleLogin = async () => {
    setError('');
    setLoading(true);
    // Record the originally requested route at click time: the OAuth round-trip
    // leaves the SPA entirely, so anything known only in React state is lost.
    // Arriving from a protected route supplies `from`; a direct visit to /login
    // falls back to the dashboard, which is the callback's own fallback.
    const from = (location.state as { from?: { pathname?: string; search?: string } } | null)?.from;
    recordPendingLoginReturn(`${from?.pathname ?? '/admin'}${from?.search ?? ''}`);
    try {
      await initiateLogin();
    } catch {
      setError(t('auth.oauth.loginFailed'));
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Brand panel */}
      <div
        className="hidden lg:flex lg:w-[360px] xl:w-[420px] flex-shrink-0 flex-col relative overflow-hidden"
        style={{ background: 'var(--navy)' }}
      >
        {/* Noise texture overlay */}
        <div
          className="absolute inset-0 opacity-[0.03] pointer-events-none"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
          }}
        />
        {/* Decorative circle */}
        <div
          className="absolute -bottom-32 -right-32 w-80 h-80 rounded-full opacity-5"
          style={{ background: 'var(--gold)' }}
        />

        <div className="relative flex flex-col h-full px-10 py-12">
          {/* Logo */}
          <div className="flex items-center gap-3 mb-auto">
            <div
              className="w-10 h-10 rounded-lg flex items-center justify-center"
              style={{ background: 'var(--gold)' }}
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M10 2L17 6V14L10 18L3 14V6L10 2Z" fill="white" fillOpacity="0.95" />
                <path d="M10 6L14 8.5V13L10 15.5L6 13V8.5L10 6Z" fill="white" fillOpacity="0.45" />
              </svg>
            </div>
            <div>
              <p
                className="text-xs font-medium tracking-widest uppercase"
                style={{ color: 'rgba(200,169,110,0.65)', fontSize: '10px' }}
              >
                Cygnus Suite
              </p>
              <p className="font-semibold text-white text-sm leading-tight">Payment</p>
            </div>
          </div>

          {/* Wordmark */}
          <div className="mt-auto mb-auto">
            <h1
              className="text-5xl font-semibold text-white leading-tight mb-4"
              style={{ fontFamily: 'var(--font-display)' }}
            >
              Payment<br />
              <span style={{ color: 'var(--gold)' }}>Admin</span>
            </h1>
            <p className="text-sm leading-relaxed" style={{ color: 'rgba(255,255,255,0.45)' }}>
              Multi-tenant payment processing<br />for Cygnus language schools.
            </p>
          </div>

          {/* Footer */}
          <p className="text-xs mt-auto" style={{ color: 'rgba(255,255,255,0.2)' }}>
            Cygnus Suite · Secured by Ory Hydra
          </p>
        </div>
      </div>

      {/* Form panel */}
      <div
        className="flex-1 flex flex-col items-center justify-center p-8 relative"
        style={{ background: 'var(--bg)' }}
      >
        {/* Language switcher */}
        <div className="absolute top-4 right-4">
          <LanguageSwitcher />
        </div>

        <div
          className="w-full max-w-sm"
          style={{ animation: 'panelIn 0.35s ease-out both' }}
        >
          {/* Mobile brand */}
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <div
              className="w-8 h-8 rounded-md flex items-center justify-center"
              style={{ background: 'var(--navy)' }}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M8 1.5L13.5 4.75V11.25L8 14.5L2.5 11.25V4.75L8 1.5Z" fill="white" fillOpacity="0.95" />
                <path d="M8 5L11 6.75V10.25L8 12L5 10.25V6.75L8 5Z" fill="white" fillOpacity="0.5" />
              </svg>
            </div>
            <span className="font-semibold text-sm" style={{ color: 'var(--text)' }}>Cygnus Payment</span>
          </div>

          <h2
            className="text-3xl font-semibold mb-1"
            style={{ fontFamily: 'var(--font-display)', color: 'var(--text)' }}
          >
            {t('auth.title')}
          </h2>
          <p className="text-sm mb-8" style={{ color: 'var(--text-muted)' }}>
            {t('auth.subtitle')}
          </p>

          {error && (
            <div
              className="px-4 py-3 rounded-md mb-6 text-sm"
              style={{
                background: 'var(--error-bg)',
                color: 'var(--error)',
                border: '1px solid rgba(192,57,43,0.2)',
                animation: 'shake 0.4s ease-out',
              }}
            >
              {error}
            </div>
          )}

          <button
            onClick={handleLogin}
            disabled={loading}
            className="w-full py-3 rounded-md text-sm font-medium text-white transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer"
            style={{ background: loading ? 'var(--navy)' : 'var(--navy)' }}
            onMouseEnter={e => { if (!loading) (e.currentTarget as HTMLButtonElement).style.background = 'var(--navy-light)' }}
            onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--navy)' }}
          >
            {loading ? (
              <>
                <div
                  className="w-4 h-4 rounded-full border-2 border-white/30 animate-spin"
                  style={{ borderTopColor: 'white' }}
                />
                {t('auth.oauth.redirecting')}
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                </svg>
                {t('auth.oauth.signInWithSSO')}
              </>
            )}
          </button>

          <p className="text-center text-xs mt-6" style={{ color: 'var(--text-muted)', opacity: 0.6 }}>
            {t('auth.footer')}
          </p>
        </div>
      </div>

      <style>{`
        @keyframes panelIn {
          from { opacity: 0; transform: translateY(16px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-6px); }
          75% { transform: translateX(6px); }
        }
      `}</style>
    </div>
  );
};
